<script setup lang="ts">
import { computed, useTemplateRef } from "vue";

import { concat } from "@/helpers/string";

import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import DropdownItem from "@/dsComponents/Dropdown/DropdownItem.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";

import { getHours } from "@/components/Mission/DateRangeTable/helper";

interface HourDropdownProps {
    value: string;
    placeholder?: string;
    icon: string;
    error?: boolean;
    scrollTo?: number;
    period?: number;
}

const props = defineProps<HourDropdownProps>();
const emit = defineEmits<(event: "select-hour", value: string) => void>();

const hourOptions = computed<{ value: string; label: string }[]>(() => {
    return getHours(props.period);
});

const items = useTemplateRef("items");

const handleToggle = (isOpen: boolean, id: string) => {
    if (isOpen && items.value) {
        // biome-ignore lint/suspicious/noExplicitAny: Init Biome
        const checkedItemIndex = items.value.findIndex((item: any) => item.checked);
        const dropdownContent = document.querySelector(`#${id} .dropdown-content`);
        // Return if we do not find the dropdown scrollable content (this should not append)
        if (!dropdownContent) return;

        // If checked item has not been found
        if (checkedItemIndex < 0) {
            // Scroll to "scrollTo" props if it has been provided
            // biome-ignore lint/style/noNonNullAssertion: Init Biome
            if (props.scrollTo) setTimeout(() => dropdownContent.scrollTo({ top: props.scrollTo! * 38 }), 100);
            return;
        }
        const top = checkedItemIndex * 38;
        if (dropdownContent) {
            setTimeout(() => dropdownContent.scrollTo({ top }), 100);
        }
    }
};

const handleInput = (value: string) => {
    let inputValue = value;

    if (/^\d{2}:\d{2}$/.test(inputValue)) {
        const [hours, minutes] = inputValue.split(":");
        inputValue = `${parseInt(hours, 10)}${minutes}`;
    }

    emit("select-hour", inputValue);
};

const time = computed(() => {
    const minutes = props.value.slice(-2);
    const hours = props.value.slice(0, -2).padStart(2, "0");
    return concat([hours, minutes], ":");
});
</script>
<template>
    <div class="HourDropdown">
        <Dropdown @toggle="handleToggle" close-on-descendant max-height="200px" max-width="200px">
            <template #trigger-zone>
                <TextInputAlt
                    variant="grey2"
                    size="xs"
                    :icon-left="icon"
                    :error="error"
                    :modelValue="time"
                    :placeholder="placeholder"
                    :state="value ? 'filled' : 'empty'"
                    @update:model-value="handleInput"
                    type="time"
                    width="75px"
                    class="HourDropdown__input"
                />
            </template>
            <template #dropdown-content>
                <DropdownItem
                    v-for="hour of hourOptions"
                    :key="hour.value"
                    ref="items"
                    :label="hour.label"
                    :checked="hour.value === value"
                    @click="emit('select-hour', hour.value)"
                />
            </template>
        </Dropdown>
    </div>
</template>
<style scoped lang="scss">
.HourDropdown {
    &:deep(.HourDropdown__input) {
        input {
            cursor: pointer;
        }
    }
}
</style>
